import type { SearchIntent } from "./search-intent.js";
import type { CatalogueIndustrialQueryPlan } from "./catalogue-industrial-search.js";

const boundary = "(?:^|[^a-z0-9])";
const end = "(?:$|[^a-z0-9])";
const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const families = {
  "Power supply": {
    family: "power_supply", candidate: "(power <-> supply) | (power <-> supplies) | sitop:* | psu:*",
    identity: "power[ -]+suppl(?:y|ies)|sitop|psu[0-9]*",
    exclude: "(?:managed|unmanaged|ethernet|ie)[ -]+switch|scalance|contactor|processor|cpu|sensor|accessor(?:y|ies)|braking[ -]+resistor|terminal[ -]+module|holding[ -]+the[ -]+power[ -]+supply|logic[ -]+module|expansion[ -]+module|redundancy[ -]+module|redundanzmodul[a-z]*|battery[ -]+module|batteriemodul|dali[ -]+master[ -]+module",
  },
  "Contactor": {
    family: "contactor", candidate: "contactor:*", identity: "contactors?",
    exclude: "accessor(?:y|ies)|auxiliary[ -]+contact[ -]+block|spare[ -]+coil|replacement[ -]+coil",
  },
  "Industrial relay": { family: "relay", candidate: "relay:*", identity: "relays?", exclude: "relay[ -]+socket|accessor(?:y|ies)|contactor|ethernet[ -]+switch|plcs?|cpu|logic[ -]+controller|relay[ -]+outputs?" },
  "Safety relay": { family: "relay", candidate: "relay:* & safety", identity: "safety[ -]+relays?", exclude: "relay[ -]+socket|accessor(?:y|ies)|contactor" },
  "Industrial switch": {
    family: "switch", candidate: "switch:* | scalance:*",
    identity: "(?:(?:industrial|managed|unmanaged|ethernet|ie)[ -]+){1,3}switch(?:es)?",
    exclude: "accessor(?:y|ies)|switch[ -]+mode[ -]+power",
  },
  "PLC": {
    family: "plc", candidate: "plc:* | cpu:* | processor:* | (programmable <-> logic <-> controller) | (software <-> controller) | (open <-> controller)",
    identity: "plcs?|cpu|processors?|programmable[ -]+(?:logic[ -]+)?controllers?|(?:software|open)[ -]+controllers?",
    exclude: "accessor(?:y|ies)|cable|connector[ -]+for[ -]+(?:plcs?|cpu)|power[ -]+suppl(?:y|ies)[ -]+for|(?:digital|analog|analogue)[ -]+(?:input|output)[ -]+module|supercap|memory[ -]+cards?[ -]+for|cpu(?:[ -]+[0-9][a-z0-9-]*)?[ -]+memory[ -]+cards?|battery[ -]+for|replacement[ -]+connector",
  },
  "PLC processor": {
    family: "plc", candidate: "cpu:* | processor:* | (programmable <-> logic <-> controller) | (software <-> controller) | (open <-> controller)",
    identity: "cpu|processors?|programmable[ -]+(?:logic[ -]+)?controllers?|(?:software|open)[ -]+controllers?",
    exclude: "accessor(?:y|ies)|cable|connector[ -]+for[ -]+(?:plcs?|cpu)|power[ -]+suppl(?:y|ies)[ -]+for|supercap|memory[ -]+cards?[ -]+for|cpu(?:[ -]+[0-9][a-z0-9-]*)?[ -]+memory[ -]+cards?|battery[ -]+for|replacement[ -]+connector",
  },
} as const;

/** Typed generic retrieval avoids legacy fuzzy counts and secondary equipment mentions. */
export function buildGenericCatalogueDiscoveryPlan(query: string, intent: SearchIntent, manufacturerId?: string): CatalogueIndustrialQueryPlan | null {
  if (intent.mode !== "product_discovery") return null;
  const family = families[intent.productFamily as keyof typeof families];
  if (!family) return null;
  if (family.family === "power_supply" && /\b(?:input|primary)\b/i.test(query)) return null;
  const requirements: CatalogueIndustrialQueryPlan["requirements"] = [
    { scope: "identity", pattern: boundary + "(?:" + family.identity + ")" + end },
  ];
  if (family.family === "power_supply") {
    // An IPC configuration can mention its included supply. A dedicated supply
    // for that computer still qualifies because its title identifies the supply.
    requirements.push({ scope: "identity", pattern: "^(?!(?:simatic[ -]+)?ipc[0-9])" });
  }
  if (family.family === "plc") {
    // Host CPU specifications alone do not identify a PLC. An affirmative
    // software/open/logic controller remains valid on an industrial PC host.
    requirements.push({ scope: "identity", pattern: "^(?![\\s\\S]*" + boundary + "(?:industrial[ -]+pc|core[ -]+i[3579])" + end + ")"
      + "|" + boundary + "(?:(?:software|open)[ -]+controllers?|(?:programmable[ -]+)?logic[ -]+controllers?)" + end });
  }
  const coilRequest = family.family === "contactor" && /\b(?:coil|control[ -]+(?:circuit[ -]+|supply[ -]+)?voltage)\b/i.test(query);
  const familyWords = new Set(["power", "supply", "supplies", "psu", "relay", "relays", "safety", "contactor", "contactors", "industrial", "ethernet", "switch", "switches", "plc", "processor", "cpu"]);
  if (coilRequest) ["coil", "control", "circuit", "voltage"].forEach(word => familyWords.add(word));
  if (intent.specifications.some(spec => spec.unit?.startsWith("V"))) ["volt", "volts"].forEach(word => familyWords.add(word));
  if (intent.specifications.some(spec => spec.unit === "A")) ["amp", "amps", "ampere", "amperes"].forEach(word => familyWords.add(word));
  for (const spec of intent.specifications) {
    if (["ports", "poles", "channels", "phases"].includes(spec.name)) {
      familyWords.add(spec.name);
      familyWords.add(spec.name.slice(0, -1));
    }
  }
  for (const keyword of intent.keywords) {
    if (!familyWords.has(keyword.toLowerCase())) {
      requirements.push({ scope: "text", pattern: boundary + escape(keyword) + end });
    }
  }
  const candidates = [`(${family.candidate})`];
  for (const spec of intent.specifications) {
    if (!/^\d+(?:\.\d+)?$/.test(spec.value)) return null;
    const value = escape(spec.value).replace("\\.", "[.,]") + (Number.isInteger(Number(spec.value)) ? "(?:[.,]0+)?" : "0*");
    const numberStart = "(?:^|[^a-z0-9.,])";
    const numberEnd = "(?:$|[^a-z0-9.,])";
    if (spec.unit) {
      const unit = spec.unit === "V" ? "v(?:[ -]*(?:ac|dc))?" : escape(spec.unit.toLowerCase()).replace("vdc", "v[ -]*dc").replace("vac", "v[ -]*ac");
      const measurement = value + "[ -]*" + unit + end;
      const scope = spec.name.startsWith("coil voltage") || (coilRequest && spec.unit.startsWith("V")) ? "coil"
        : spec.name.startsWith("rated current") ? "current"
          : family.family === "power_supply" && (spec.name === "voltage" || spec.unit === "A") ? "output" : "text";
      requirements.push({ scope, pattern: numberStart + measurement });
      if (scope === "output") {
        // Unlabelled title ratings remain useful when their role is unknown.
        // Once input evidence exists, demand an affirmative output rating too.
        requirements.push({ scope: "text", pattern: "^(?![\\s\\S]*" + boundary + "(?:input|primary)" + end + ")"
          + "|" + boundary + "(?:output|secondary)(?=$|[^a-z0-9])[^;\\n]*" + numberStart + measurement });
      }
    } else if (["ports", "poles", "channels", "phases"].includes(spec.name)) {
      const label = spec.name.slice(0, -1) + "s?";
      requirements.push({ scope: "text", pattern: "(?:" + numberStart + value + "[ -]*" + label + end
        + "|" + boundary + "(?:number[ -]+of[ -]+)?" + label + "[ :]*" + value + numberEnd + ")" });
    } else return null;
    // Keep the GIN candidate set selective before evaluating detailed evidence.
    candidates.push(`('${spec.value}' | '${spec.value}.0':* | '${spec.value}${(spec.unit || "").toLowerCase()}':*)`);
  }
  return { version: 1, family: family.family, candidateTsquery: candidates.join(" & "),
    requirements, excludeIdentity: boundary + "(?:" + family.exclude + ")" + end,
    ...(manufacturerId ? { manufacturerId } : {}) };
}
